import { Injectable, computed, inject } from '@angular/core'
import { HttpClient, httpResource } from '@angular/common/http'
import { CanActivateFn, Router } from '@angular/router'
import { firstValueFrom } from 'rxjs'

import { environment } from '../../../environments/environment'
import { ApiResponse } from '../interfaces/api.interface'
import { AuthService } from '../../auth/services/auth.service'

interface Access {
  can_manage: boolean
}

/**
 * Whether the signed-in user may manage match videos -- the Video inbox and "Add match video".
 * Admins and superusers may; anyone else only when granted (CC, 28/09/2026: CC, Andreas, Marios,
 * Christos). The server decides and enforces it on every request; this only drives the menu entry
 * and the page guard.
 */
@Injectable({ providedIn: 'root' })
export class MatchVideoAccessService {
  private http = inject(HttpClient)
  private auth = inject(AuthService)

  private resource = httpResource<boolean>(
    () => (this.auth.currentUser() ? `${environment.apiUrl}/match-videos/access` : undefined),
    { defaultValue: false, parse: (raw: unknown) => !!(raw as ApiResponse<Access>).data?.can_manage },
  )

  readonly canManage = computed(() => (this.resource.hasValue() ? this.resource.value() : false))

  async check (): Promise<boolean> {
    try {
      const res = await firstValueFrom(this.http.get<ApiResponse<Access>>(`${environment.apiUrl}/match-videos/access`))
      return !!res.data?.can_manage
    } catch {
      return false
    }
  }
}

export const matchVideoGuard: CanActivateFn = async () => {
  const allowed = await inject(MatchVideoAccessService).check()
  return allowed ? true : inject(Router).parseUrl('/unauthorized')
}
